import { cn } from '@/lib/utils';
import { type Column, type Table as TanstackTable, flexRender } from '@tanstack/react-table';
import * as React from 'react';
import { Children, cloneElement, isValidElement } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'ui/table';
import CrumbTitle from '../crumbTitle/CrumbTitle';
import { getCommonPinningStyles } from './lib';

interface DataTableProps<TData> extends React.ComponentProps<'div'> {
  table: TanstackTable<TData> & {
    options: {
      meta?: { loading?: boolean };
    };
  };
  children?:
    | React.ReactElement<{ table: TanstackTable<TData> }>
    | React.ReactElement<{ table: TanstackTable<TData> }>[];
}
export function DataTable<TData>({ table, children, className, ...props }: DataTableProps<TData>) {
  const isSerialNumberColumn = (column: Column<TData>) =>
    column.id === 'sn' || (column.id === 'id' && column.getIndex() === 0);

  const childrenWithTableInstance = Children.map(children, (child) => {
    if (isValidElement<{ table: TanstackTable<TData> }>(child)) {
      return cloneElement(child, { table });
    }
    return child;
  });

  return (
    <div className={cn('flex bg-card w-full flex-col gap-2.5 overflow-hidden rounded-lg', className)} {...props}>
      {childrenWithTableInstance}
      <div className="overflow-hidden rounded-md">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSerialNumber = isSerialNumberColumn(header.column);

                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cn(
                        header.column.columnDef.meta?.align === 'right' ? 'text-right' : 'text-left',
                        isSerialNumber && 'w-px whitespace-nowrap px-3',
                      )}
                      style={{
                        ...getCommonPinningStyles({ column: header.column }),
                        width: isSerialNumber ? '1%' : header.column.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.options.meta?.loading ? (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
                  <div className="flex justify-center items-center w-full h-full">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-foreground-label border-t-transparent"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    row.depth === 0 && row.getIsExpanded() && 'bg-surface-selected',
                    row.depth > 0 && row.index % 2 === 0 && 'bg-card',
                    row.depth > 0 && row.index % 2 !== 0 && 'bg-surface-subtle',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isSerialNumber = isSerialNumberColumn(cell.column);

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          cell.column.columnDef.meta?.align === 'right' ? 'text-right' : 'text-left',
                          isSerialNumber && 'w-px whitespace-nowrap px-3',
                          'py-4',
                        )}
                        style={{
                          ...getCommonPinningStyles({ column: cell.column }),
                          width: isSerialNumber ? '1%' : cell.column.getSize(),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
                  <CrumbTitle id="common.page.table.noResult" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
